import { useCallback, useEffect, useRef, useState } from "react";
import type { GameWorldId } from "./GameWorld.tsx";
import type { DiscoveryGameId } from "../shared/discovery-game.ts";

const SOUND_STORAGE_KEY = "neckmoves:game-sound";
export type GameSoundCue = "start" | "arrive" | "center" | "rest" | "complete" | "care" | "note-0" | "note-1" | "note-2";

const VOICES: Record<GameWorldId | DiscoveryGameId, { notes: number[]; type: OscillatorType; decay: number }> = {
  "forest-post": { notes: [262, 330, 392, 523], type: "triangle", decay: .24 },
  "reef-lens": { notes: [392, 494, 587, 784], type: "sine", decay: .42 },
  "moon-harp": { notes: [262, 330, 392, 523], type: "triangle", decay: 1.2 },
  firefly: { notes: [659, 784, 880, 1047, 1175], type: "sine", decay: .7 },
  boat: { notes: [294, 392, 440, 587], type: "sine", decay: .28 },
  greenhouse: { notes: [523, 659, 784, 988], type: "sine", decay: .48 },
  lighthouse: { notes: [196, 247, 294, 392], type: "triangle", decay: .9 },
  "cloud-trail": { notes: [392, 523, 659, 784], type: "sine", decay: .65 },
  "star-path": { notes: [587, 740, 880, 1175, 1480], type: "sine", decay: 1.1 },
};

export function useGameAudio(game: GameWorldId | DiscoveryGameId = "firefly") {
  const [enabled, setEnabled] = useState(() => {
    try { return window.localStorage.getItem(SOUND_STORAGE_KEY) !== "off"; } catch { return true; }
  });
  const enabledRef = useRef(enabled);
  const contextRef = useRef<AudioContext | null>(null);
  const unlockedRef = useRef(false);
  const sourcesRef = useRef(new Set<AudioScheduledSourceNode>());
  const noteRef = useRef(0);

  const context = useCallback(() => {
    try {
      contextRef.current ??= new AudioContext();
      if (contextRef.current.state === "suspended") void contextRef.current.resume().catch(() => {});
      return contextRef.current;
    } catch { return null; }
  }, []);

  const stop = useCallback(() => {
    for (const source of sourcesRef.current) { try { source.stop(); } catch { /* Already ended. */ } }
    sourcesRef.current.clear();
  }, []);

  const play = useCallback((cue: GameSoundCue) => {
    if (!enabledRef.current || !unlockedRef.current) return;
    if (typeof document !== "undefined" && document.hidden) return;
    const audio = contextRef.current;
    if (!audio || audio.state !== "running") return;
    const voice = VOICES[game];
    const base = voice.notes[noteRef.current++ % voice.notes.length];
    const notes = cue.startsWith("note-") ? [voice.notes[Number(cue.slice(-1))]] : cue === "complete" ? voice.notes.slice(0, 4) : cue === "rest" ? [262, 330]
      : cue === "start" ? voice.notes.slice(0, 2) : cue === "center" ? [base * .75, base]
        : game === "lighthouse" ? [base, base * 1.5] : [base];
    notes.forEach((frequency, index) => {
      const oscillator = audio.createOscillator();
      const gain = audio.createGain();
      const start = audio.currentTime + index * 0.15;
      const decay = cue === "complete" ? 1 : voice.decay;
      oscillator.type = voice.type;
      const bubble = game === "boat" || (game === "reef-lens" && cue === "arrive");
      oscillator.frequency.setValueAtTime(bubble ? frequency * 1.8 : frequency, start);
      if (bubble) oscillator.frequency.exponentialRampToValueAtTime(frequency, start + .12);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.045, start + 0.018);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + decay);
      oscillator.connect(gain).connect(audio.destination);
      sourcesRef.current.add(oscillator);
      oscillator.onended = () => { sourcesRef.current.delete(oscillator); oscillator.disconnect(); gain.disconnect(); };
      oscillator.start(start);
      oscillator.stop(start + decay + .02);
    });
    if ((game === "cloud-trail" || (game === "greenhouse" && cue === "care") || (game === "forest-post" && cue === "arrive") || (game === "reef-lens" && cue === "center")) && cue !== "rest") {
      const duration = game === "reef-lens" ? .09 : game === "forest-post" ? .18 : .45;
      const length = Math.floor(audio.sampleRate * duration);
      const buffer = audio.createBuffer(1, length, audio.sampleRate);
      const samples = buffer.getChannelData(0);
      for (let i = 0; i < length; i++) samples[i] = Math.random() * 2 - 1;
      const source = audio.createBufferSource();
      const filter = audio.createBiquadFilter();
      const gain = audio.createGain();
      source.buffer = buffer;
      filter.type = "lowpass";
      filter.frequency.value = game === "cloud-trail" ? 900 : game === "reef-lens" ? 2800 : 1700;
      gain.gain.setValueAtTime(.0001, audio.currentTime);
      gain.gain.exponentialRampToValueAtTime(.025, audio.currentTime + duration * .2);
      gain.gain.exponentialRampToValueAtTime(.0001, audio.currentTime + duration - .01);
      source.connect(filter).connect(gain).connect(audio.destination);
      sourcesRef.current.add(source);
      source.onended = () => { sourcesRef.current.delete(source); source.disconnect(); filter.disconnect(); gain.disconnect(); };
      source.start();
      source.stop(audio.currentTime + duration);
    }
  }, [game]);

  const unlock = useCallback(() => {
    unlockedRef.current = true;
    noteRef.current = 0;
    if (enabledRef.current) context();
  }, [context]);

  const toggle = useCallback(() => {
    const next = !enabledRef.current;
    enabledRef.current = next;
    setEnabled(next);
    try { window.localStorage.setItem(SOUND_STORAGE_KEY, next ? "on" : "off"); } catch { /* Session preference still works. */ }
    if (next) { unlockedRef.current = true; context(); } else stop();
  }, [context, stop]);

  useEffect(() => {
    const hidden = () => { if (document.hidden) stop(); };
    window.addEventListener("blur", stop);
    document.addEventListener("visibilitychange", hidden);
    return () => {
      window.removeEventListener("blur", stop);
      document.removeEventListener("visibilitychange", hidden);
      stop();
      if (contextRef.current) void contextRef.current.close().catch(() => {});
      contextRef.current = null;
    };
  }, [stop]);

  return { enabled, play, unlock, toggle, stop };
}
